import { scrubText, scrubUrl } from './scrubUrl';

interface ScrubbableBreadcrumb {
  message?: string;
  data?: Record<string, unknown>;
}

interface ScrubbableSpan {
  description?: string;
  data?: Record<string, unknown>;
}

interface ScrubbableEvent {
  message?: string;
  transaction?: string;
  request?: { url?: string; query_string?: unknown; headers?: Record<string, string> };
  exception?: { values?: Array<{ value?: string }> };
  contexts?: { trace?: { data?: Record<string, unknown> } };
  breadcrumbs?: ScrubbableBreadcrumb[];
  spans?: ScrubbableSpan[];
}

const URL_DATA_KEYS = [
  'url',
  'to',
  'from',
  'http.url',
  'url.full',
  'url.query',
  'http.query',
  'url.fragment',
  'http.fragment',
] as const;

function scrubData(data: Record<string, unknown> | undefined): Record<string, unknown> | undefined {
  if (!data) return data;
  const out = { ...data };
  for (const key of URL_DATA_KEYS) {
    if (typeof out[key] === 'string') out[key] = scrubUrl(out[key]);
  }
  if (Array.isArray(out.arguments)) {
    out.arguments = out.arguments.map((a: unknown) => (typeof a === 'string' ? scrubText(a) : a));
  }
  return out;
}

/** Fetch/xhr breadcrumbs carry `url`; navigation breadcrumbs carry `to`/`from`. */
export function scrubBreadcrumb<T extends ScrubbableBreadcrumb>(crumb: T): T {
  const out = { ...crumb };
  if (crumb.data) out.data = scrubData(crumb.data);
  if (crumb.message) out.message = scrubText(crumb.message);
  return out;
}

function scrubSpan(span: ScrubbableSpan): ScrubbableSpan {
  const out = { ...span };
  if (span.description) out.description = scrubText(scrubUrl(span.description));
  if (span.data) out.data = scrubData(span.data);
  return out;
}

export function scrubSentryEvent<T extends ScrubbableEvent>(event: T): T {
  const out: ScrubbableEvent = { ...event };
  if (event.message) out.message = scrubText(event.message);
  if (event.transaction) out.transaction = scrubUrl(event.transaction);
  if (event.request) {
    const request = { ...event.request };
    if (request.url) request.url = scrubUrl(request.url);
    if (request.query_string) delete request.query_string;
    if (request.headers?.Referer) {
      request.headers = { ...request.headers, Referer: scrubUrl(request.headers.Referer) };
    }
    out.request = request;
  }
  if (event.exception?.values) {
    out.exception = {
      ...event.exception,
      values: event.exception.values.map((v) => (v.value ? { ...v, value: scrubText(v.value) } : v)),
    };
  }
  if (event.breadcrumbs) out.breadcrumbs = event.breadcrumbs.map(scrubBreadcrumb);
  if (event.contexts?.trace?.data) {
    out.contexts = {
      ...event.contexts,
      trace: { ...event.contexts.trace, data: scrubData(event.contexts.trace.data) },
    };
  }
  if (event.spans) out.spans = event.spans.map(scrubSpan);
  return out as T;
}

interface ScrubbableLog {
  message?: unknown;
  attributes?: Record<string, unknown>;
}

function scrubAttribute(attr: unknown): unknown {
  if (typeof attr === 'string') return scrubText(attr);
  if (attr && typeof attr === 'object' && typeof (attr as { value?: unknown }).value === 'string') {
    return { ...attr, value: scrubText((attr as { value: string }).value) };
  }
  return attr;
}

export function scrubLog<T extends ScrubbableLog>(log: T): T {
  const out = { ...log };
  if (typeof log.message === 'string') out.message = scrubText(log.message);
  if (log.attributes) {
    out.attributes = Object.fromEntries(
      Object.entries(log.attributes).map(([k, v]) => [k, scrubAttribute(v)]),
    );
  }
  return out;
}

interface ScrubbableReplayEvent {
  type?: string;
  urls?: string[];
  request?: { url?: string };
}

/** Replay events skip beforeSend; register with Sentry.addEventProcessor. */
export function scrubReplayEvent<T extends ScrubbableReplayEvent>(event: T): T {
  if (event.type !== 'replay_event') return event;
  const out = { ...event };
  if (event.urls) out.urls = event.urls.map((u) => scrubUrl(u));
  if (event.request?.url) out.request = { ...event.request, url: scrubUrl(event.request.url) };
  return out;
}

interface ScrubbableReplayFrame {
  data?: unknown;
}

interface SnapshotNode {
  attributes?: Record<string, unknown>;
  childNodes?: SnapshotNode[];
  textContent?: string;
}

interface ReplayFrameData {
  href?: string;
  node?: SnapshotNode;
  adds?: ({ node?: SnapshotNode } | null)[];
  attributes?: ({ attributes?: Record<string, unknown> } | null)[];
  texts?: ({ value?: string | null } | null)[];
  payload?: { description?: string; message?: string; data?: Record<string, unknown> };
}

const URL_ATTRIBUTES = ['href', 'src', 'action'] as const;

function scrubSrcset(value: string): string {
  return value
    .split(',')
    .map((candidate) => {
      const [url = '', ...descriptor] = candidate.trim().split(/\s+/);
      return [scrubUrl(url), ...descriptor].join(' ');
    })
    .join(', ');
}

function scrubUrlAttributes(source: Record<string, unknown>): Record<string, unknown> {
  const attributes = { ...source };
  for (const name of URL_ATTRIBUTES) {
    if (typeof attributes[name] === 'string') attributes[name] = scrubUrl(attributes[name]);
  }
  if (typeof attributes.srcset === 'string') attributes.srcset = scrubSrcset(attributes.srcset);
  return attributes;
}

function scrubSnapshotNode(node: SnapshotNode): SnapshotNode {
  const out = { ...node };
  if (typeof node.textContent === 'string') out.textContent = scrubText(node.textContent);
  if (node.attributes) out.attributes = scrubUrlAttributes(node.attributes);
  if (node.childNodes) out.childNodes = node.childNodes.map(scrubSnapshotNode);
  return out;
}

/** For replayIntegration({ beforeAddRecordingEvent }): scrubs Meta hrefs, snapshot href/src attributes and breadcrumb/span payloads. */
export function scrubReplayFrame<T extends ScrubbableReplayFrame>(frame: T): T {
  if (!frame.data || typeof frame.data !== 'object') return frame;
  const data: ReplayFrameData = { ...(frame.data as ReplayFrameData) };
  if (typeof data.href === 'string') data.href = scrubUrl(data.href);
  if (data.node) data.node = scrubSnapshotNode(data.node);
  if (Array.isArray(data.adds)) {
    data.adds = data.adds.map((add) => (add?.node ? { ...add, node: scrubSnapshotNode(add.node) } : add));
  }
  if (Array.isArray(data.attributes)) {
    data.attributes = data.attributes.map((change) =>
      change?.attributes ? { ...change, attributes: scrubUrlAttributes(change.attributes) } : change,
    );
  }
  if (Array.isArray(data.texts)) {
    data.texts = data.texts.map((text) =>
      typeof text?.value === 'string' ? { ...text, value: scrubText(text.value) } : text,
    );
  }
  if (data.payload) {
    const payload = { ...data.payload };
    if (payload.description) payload.description = scrubText(scrubUrl(payload.description));
    if (payload.message) payload.message = scrubText(payload.message);
    if (payload.data) payload.data = scrubData(payload.data);
    data.payload = payload;
  }
  return { ...frame, data };
}

export const replayPrivacyOptions = { beforeAddRecordingEvent: scrubReplayFrame };

/** Spread into Sentry.init in every app so the privacy settings cannot drift. */
export const sentryPrivacyOptions = {
  sendDefaultPii: false,
  beforeSend: scrubSentryEvent,
  beforeSendTransaction: scrubSentryEvent,
  beforeBreadcrumb: scrubBreadcrumb,
  beforeSendLog: scrubLog,
};
